import React, { Suspense } from 'react';
import styled from 'styled-components';
import {
  Button,
  InformationLayer,
  InformationLayerTitle,
  RawHtml,
  Text,
} from '@sparknz/spark-pattern-library';

import ContentLayerLoader from 'shared/components/ContentLayerLoader';
import { ACTION_LAYER_ELEMENT_ID } from 'shared/constants/elementSelector.constants';
import useElementPresence from 'shared/hooks/useElementPresence/useElementPresence.hook';
import useQuery from 'shared/hooks/useQuery/useQuery.hook';
import { usePageConfig } from 'shared/providers/PageConfigProvider';
import { ContentLayerProps } from './ContentInfoLayerEditConfig';
import useContent from './hooks/useContent';
import { Refetch } from './hooks/useContent/useContent';

interface ContentBodyProps {
  message: string;
  buttonText: string;
  value: string;
  baseUrl: string;
  variation: string;
}
const ContentInfoLayerEditor = styled.div`
  border: 1px solid black;
  padding: 24px;
`;

const ContentBody = ({
  value,
  baseUrl,
  variation,
  buttonText,
  message,
}: ContentBodyProps) => {
  const { content, isError, refetch } = useContent({
    key: value,
    baseUrl,
    variation,
  });

  if (!content || isError) {
    return (
      <Error
        message={message}
        buttonText={buttonText}
        value={value}
        baseUrl={baseUrl}
        variation={variation}
        refetch={refetch}
      />
    );
  }

  return (
    <>
      <InformationLayerTitle>{content?.title}</InformationLayerTitle>
      <RawHtml variant="bodySMedium" html={content?.content.html || ''} />
    </>
  );
};

interface ErrorProps extends ContentBodyProps {
  refetch: Refetch;
}

const Error = ({ message, buttonText, refetch }: ErrorProps) => {
  return (
    <>
      <Text variant="bodySMedium" mb="s4">
        {message}
      </Text>
      {buttonText ? (
        <Button
          variant="default"
          onClick={() => {
            refetch();
          }}
        >
          {buttonText}
        </Button>
      ) : null}
    </>
  );
};

interface ConstructContentUrlProps {
  headlessCmsUrl: string;
  modelId: string;
  persistedQueryName: string;
  configuration: string;
}
const constructContentUrl = ({
  headlessCmsUrl,
  modelId,
  persistedQueryName,
  configuration,
}: ConstructContentUrlProps) => {
  return `${headlessCmsUrl}/${configuration}/${persistedQueryName}/models/${modelId}`;
};

const ContentInfoLayer = ({
  errorText,
  modelId,
  isInEditor,
  persistedQueryName,
  retryButtonText,
  triggerKey,
  configuration,
  variation,
}: ContentLayerProps) => {
  const { query, replaceQuery } = useQuery();

  const value = query?.[triggerKey];
  const isNested = useElementPresence(`#${ACTION_LAYER_ELEMENT_ID}`);

  const isOpen = Boolean(value);

  const { headlessCmsUrl } = usePageConfig();

  const baseUrl = constructContentUrl({
    headlessCmsUrl,
    configuration,
    modelId,
    persistedQueryName,
  });

  function onClose() {
    replaceQuery({ ...query, [triggerKey]: null });
  }

  if (isInEditor) {
    return (
      <ContentInfoLayerEditor>
        <Text variant="bodyMDemi">
          Information Layer with props{' '}
          {JSON.stringify({
            errorText,
            modelId,
            persistedQueryName,
            retryButtonText,
            triggerKey,
            variation,
            isNested,
          })}
        </Text>
      </ContentInfoLayerEditor>
    );
  }

  return (
    <InformationLayer isOpen={isOpen} isNested={isNested} onClose={onClose}>
      {value && (
        <Suspense fallback={<ContentLayerLoader />}>
          <ContentBody
            value={value as string}
            baseUrl={baseUrl}
            variation={variation}
            buttonText={retryButtonText}
            message={errorText}
          />
        </Suspense>
      )}
    </InformationLayer>
  );
};

export default ContentInfoLayer;
